import React, { useEffect, useRef } from 'react';

import { ReactEditor, useSlateStatic } from 'slate-react';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import HelptapEditor from '../../helpers';

import nodeControls from '../../node-menu/control-types';

import BaseElement from '../base';
import GalleryContext from './context';

import './index.scss';

function Gallery(props) {

  const element = props.element;
  const properties = element.properties || {};
  const galleryId = properties.galleryId || null;

  const settings = HelptapEditor.gallery.getSettings(element);

  const editor = useSlateStatic();

  const creatingReference = useRef(false);

  useEffect(function onLoad() {

    tryCreateGallery();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId]);

  async function tryCreateGallery() {

    if (galleryId !== null) {
      return;
    }

    if (creatingReference.current === true) {
      return;
    }

    creatingReference.current = true;

    try {

      const gallery = await serverInterface.startup.pages.createGallery(props.parentEntityId);

      const elementPath = ReactEditor.findPath(editor, props.element);

      HelptapEditor.setAttributes(editor, {
        properties: {
          ...properties,
          galleryId: gallery._id
        }
      }, elementPath);

    } catch (exception) {

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Failed to create gallery. Please try again!',
        seconds: 5,
        type: 'error'
      });

    } finally {

      creatingReference.current = false;

    }

  }

  async function handleAddImagesClick() {

    const result = await application.domUtility.askFiles({ type: 'image/*', limit: 10 });

    if (result.cancelled === true) {
      return;
    }

    const elementPath = ReactEditor.findPath(editor, props.element);

    HelptapEditor.gallery.appendItems(editor, elementPath, galleryId, result.files);

  }

  function renderAddImagesButton() {

    const buttonAttributes = {
      type: 'button',
      className: 'gallery-add-images-button',
      contentEditable: false,
      onMouseDown(event) {
        event.preventDefault();
      },
      onClick() {
        handleAddImagesClick();
      }
    };

    return <button {...buttonAttributes}>+ Add images</button>;

  }

  function renderGalleryContent() {

    const gridAttributes = {
      className: 'gallery-grid',
      style: HelptapEditor.gallery.getContainerStyleObject(settings)
    };

    const properties = {
      ...props.attributes,
      className: 'gallery-main'
    };

    return <div {...properties}>
      <div {...gridAttributes}>
        {props.children}
      </div>
      {renderAddImagesButton()}
    </div>;

  }

  function renderMain() {

    const baseElementProperties = {
      ...props,
      placeholder: '',
      className: 'gallery-element-main',
      customNodeControls: [nodeControls.GALLERY_SETTINGS]
    };

    const contextValue = {
      galleryId,
      captionEnabled: settings.captionEnabled,
      captionPosition: settings.captionPosition,
      captionAppearance: settings.captionAppearance
    };

    return <GalleryContext.Provider value={contextValue}>
      <BaseElement {...baseElementProperties}>
        {renderGalleryContent()}
      </BaseElement>
    </GalleryContext.Provider>;

  }

  return renderMain();

}

export default Gallery;